import stripAnsi from 'strip-ansi';
import type { ProjectError } from './plugins/events.js';

interface BuildErrorLocation {
  column: number;
  file?: string;
  line: number;
}

interface EsbuildMessage {
  column: number;
  file: string;
  line: number;
  text: string;
}

const isBuildErrorLocation = (obj: unknown): obj is BuildErrorLocation => {
  return (
    typeof obj === 'object' &&
    obj != null &&
    'line' in obj &&
    typeof obj.line === 'number' &&
    'column' in obj &&
    typeof obj.column === 'number' &&
    ('file' in obj ? typeof obj.file === 'string' : true)
  );
};

const getEsbuildMessages = (e: Error): EsbuildMessage[] => {
  if (!('errors' in e) || !Array.isArray(e.errors)) {
    return [];
  }

  const messages: EsbuildMessage[] = [];
  for (const message of e.errors) {
    if (typeof message !== 'object' || message == null) {
      continue;
    }
    if (!('text' in message) || typeof message.text !== 'string') {
      continue;
    }
    if (!('location' in message) || !isBuildErrorLocation(message.location)) {
      continue;
    }
    if (message.location.file == null) {
      continue;
    }

    messages.push({
      column: message.location.column,
      file: message.location.file,
      line: message.location.line,
      text: message.text,
    });
  }

  return messages;
};

// Vite rebuilds a build error's stack as `<message>\n<stack frames>` after
// having rebuilt the message itself into a banner (plugin tag, `file:` line,
// code frame). Slicing the message prefix off recovers the pure stack frames;
// the code frame is then restored from the error's `frame` field.
const getStack = (e: Error): string | undefined => {
  if (e.stack == null) {
    return undefined;
  }

  let stack = stripAnsi(e.stack);
  const message = stripAnsi(e.message);
  for (const prefix of [`${message}\n`, `${e.name}: ${message}\n`]) {
    if (!stack.startsWith(prefix)) {
      continue;
    }
    stack = stack.slice(prefix.length);
    break;
  }

  const frame =
    'frame' in e && typeof e.frame === 'string' ? stripAnsi(e.frame) : null;
  if (frame == null) {
    return stack;
  }

  return `${frame.trim()}\n\n${stack}`;
};

// Parsing error is defensive by design.
// It should not break, since we're using it in error handling.
export const parseBuildError = (e: unknown): ProjectError[] | null => {
  try {
    if (!(e instanceof Error)) {
      return null;
    }

    const raw = stripAnsi(e.message);
    const stack = getStack(e);

    const messages = getEsbuildMessages(e);
    if (messages.length > 0) {
      return messages.map(({ column, file, line, text }) => ({
        message: text,
        location: { file, line, column },
        ...(stack != null && { stack }),
      }));
    }

    const loc = 'loc' in e ? e.loc : null;
    if (!isBuildErrorLocation(loc)) {
      return null;
    }

    // Rollup locations may omit `file` when a plugin reports only a line and
    // column; in that case the module path is stored in the top-level `id`.
    // Vite's own error formatter uses the same `loc.file` -> `id` fallback.
    const id = 'id' in e && typeof e.id === 'string' ? e.id : null;
    const file = loc.file ?? id;
    if (file == null) {
      return null;
    }

    return [
      {
        message: raw,
        location: {
          file,
          line: loc.line,
          column: loc.column,
        },
        ...(stack != null && { stack }),
      },
    ];
  } catch {
    return null;
  }
};
